import { Link, useLocation, useSearch } from "wouter";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  LayoutDashboard,
  Mic,
  PenTool,
  Wand2,
  Wrench,
  Archive,
  Settings,
  Pencil,
  Mail,
  LogIn,
  Sparkles,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store";
import { ScribPostLogo } from "@/components/scribpost-logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const NAV_SECTIONS = [
  {
    label: "Core",
    items: [
      { href: "/studio",    label: "Studio",          icon: Wand2           },
      { href: "/",          label: "Dashboard",       icon: LayoutDashboard },
      { href: "/generator", label: "Post Generator",  icon: PenTool         },
      { href: "/reply",     label: "Smart Message",   icon: Mail            },
    ],
  },
  {
    label: "Training",
    items: [
      { href: "/training", label: "Voice & Text", icon: Mic },
    ],
  },
  {
    label: "Tools",
    items: [
      { href: "/tools", label: "Creator Tools", icon: Wrench  },
      { href: "/vault", label: "Idea Vault",    icon: Archive },
    ],
  },
];

function NavItem({ href, label, icon: Icon, isActive }: {
  href: string;
  label: string;
  icon: React.ElementType;
  isActive: boolean;
}) {
  return (
    <Link href={href} className="block relative">
      <div
        className={cn(
          "flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium transition-all duration-200 group relative z-10 text-sm",
          isActive
            ? "text-primary-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
        )}
      >
        <Icon
          className={cn(
            "w-4 h-4 shrink-0",
            isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-foreground"
          )}
        />
        {label}
      </div>
      {isActive && (
        <motion.div
          layoutId="sidebar-active"
          className="absolute inset-0 rounded-xl z-0 sidebar-active-glow"
          style={{ background: "linear-gradient(135deg, hsl(188 90% 42%) 0%, hsl(205 88% 52%) 100%)" }}
          transition={{ type: "spring", stiffness: 320, damping: 32 }}
        />
      )}
    </Link>
  );
}

function itemIsActive(location: string, _search: string, href: string): boolean {
  if (href === "/training") {
    return location === "/training";
  }
  return location === href;
}

export function Sidebar() {
  const [location] = useLocation();
  const search = useSearch();
  const { displayName, setDisplayName } = useAppStore();
  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState("");

  const isSubscribed = useAppStore((s) => s.isSubscribed);
  const postsGeneratedCount = useAppStore((s) => s.postsGeneratedCount);
  const authToken = useAppStore((s) => s.authToken);
  const hasCompletedOnboarding = useAppStore((s) => s.hasCompletedOnboarding);

  const greeting = displayName || "You";
  const initial  = displayName ? displayName.charAt(0).toUpperCase() : "?";

  const openEdit = () => { setEditValue(displayName || ""); setEditOpen(true); };
  const saveEdit = () => { setDisplayName(editValue.trim()); setEditOpen(false); };

  const freeUsed = Math.min(postsGeneratedCount, 15);
  const freePct  = Math.min((postsGeneratedCount / 15) * 100, 100);
  const postsLeft = Math.max(0, 15 - postsGeneratedCount);
  const showTrialCard = !isSubscribed && postsGeneratedCount > 0;

  return (
    <>
      <aside
        aria-label="Main navigation"
        className="w-60 border-r border-border/50 flex flex-col h-screen sticky top-0 left-0 hidden md:flex"
        style={{
          background: "hsl(222 44% 6% / 0.80)",
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
        }}
      >
        {/* Logo */}
        <div className="px-5 pt-5 pb-3">
          <ScribPostLogo />
        </div>

        {/* Profile card */}
        <div className="mx-3 mb-3 rounded-xl bg-secondary/40 border border-border/40 flex items-center gap-2.5 p-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0 shadow-md shadow-primary/25">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate leading-tight">
              {greeting}
              {isSubscribed && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold text-primary-foreground" style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}>
                  PRO
                </span>
              )}
            </p>
            <button
              onClick={openEdit}
              className="text-[10px] text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-0.5"
            >
              <Pencil className="w-2.5 h-2.5" />
              Edit name
            </button>
          </div>
        </div>

        {/* Open Studio CTA */}
        <div className="px-3 mb-3">
          <Link href="/studio" className="block">
            <div
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-primary/35 hover:brightness-110"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
            >
              <Wand2 className="w-4 h-4" />
              Open Studio
            </div>
          </Link>
        </div>

        {/* Sectioned navigation */}
        <nav className="flex-1 px-3 overflow-y-auto space-y-3 mt-0.5">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="px-3.5 mb-1 text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItem
                    key={item.href}
                    href={item.href}
                    label={item.label}
                    icon={item.icon}
                    isActive={itemIsActive(location, search, item.href)}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Log in button (unauthenticated users) */}
        {!authToken && !hasCompletedOnboarding && (
          <div className="px-3 mt-2">
            <Link href="/login" className="block">
              <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm text-primary border border-primary/30 hover:bg-primary/10 transition-colors">
                <LogIn className="w-4 h-4" />
                Log in
              </div>
            </Link>
          </div>
        )}

        {/* Trial upgrade card */}
        {showTrialCard && (
          <div className="mx-3 mt-2 px-3 py-3 rounded-xl border border-primary/20 bg-gradient-to-b from-primary/5 to-accent/5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3 h-3 text-primary" />
                <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wide">Free trial</span>
              </div>
              <span className="text-[10px] font-semibold text-muted-foreground">
                {freeUsed}/15 actions
              </span>
            </div>
            <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden mb-2.5">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${freePct}%`,
                  background: postsGeneratedCount >= 13
                    ? "hsl(0 80% 55%)"
                    : postsGeneratedCount >= 9
                    ? "hsl(38 92% 50%)"
                    : "linear-gradient(90deg, hsl(188 90% 42%), hsl(205 88% 52%))",
                }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground/60 mb-2.5">
              {postsLeft > 0
                ? `${postsLeft} free action${postsLeft === 1 ? "" : "s"} remaining`
                : "Free actions used up"}
            </p>
            <Link href="/pricing" className="block">
              <div
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold text-primary-foreground transition-all hover:brightness-110"
                style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
              >
                <Sparkles className="w-3 h-3" />
                Upgrade to Pro
              </div>
            </Link>
          </div>
        )}

        {/* Settings — pinned at bottom */}
        <div className="px-3 pb-4 mt-2">
          <div className="h-px bg-border/50 mb-2 mx-1" />
          <NavItem
            href="/settings"
            label="Settings"
            icon={Settings}
            isActive={location === "/settings"}
          />
        </div>
      </aside>

      {/* Edit name dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Change your name</DialogTitle>
            <DialogDescription>
              This name is shown in the sidebar and dashboard greeting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && editValue.trim()) saveEdit(); }}
              placeholder="Your first name"
              className="h-11 rounded-xl"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button size="sm" disabled={!editValue.trim()} onClick={saveEdit}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
